<template>
  <div v-if="gate.required" class="age-gate" role="dialog" aria-modal="true" aria-labelledby="age-gate-title">
    <div class="age-gate__panel">
      <div class="age-gate__badge">16+</div>

      <template v-if="!gate.blocked">
        <h1 id="age-gate-title" class="age-gate__title">This site is for viewers aged 16 and above</h1>
        <p class="age-gate__text">
          DigiLok OTT streams short dramas rated <strong>16+</strong> — mature themes, strong language
          and romantic content intended for adult audiences.
        </p>
        <p class="age-gate__text age-gate__text--sm">
          By entering you confirm that you are at least 16 years old and consent to viewing this content.
        </p>
        <div class="age-gate__actions">
          <button class="age-gate__btn age-gate__btn--primary" @click="gate.confirmAge()">
            I am 16 or older — Enter
          </button>
          <button class="age-gate__btn age-gate__btn--ghost" @click="gate.declineAge()">
            I am under 16
          </button>
        </div>
        <p class="age-gate__legal">
          <a href="/terms-of-use" target="_blank" rel="noopener">Terms of Use</a>
          <span class="age-gate__sep">·</span>
          <a href="/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>
          <span class="age-gate__sep">·</span>
          <a href="/content-moderation-policy" target="_blank" rel="noopener">Content Moderation</a>
        </p>
      </template>

      <template v-else>
        <h1 id="age-gate-title" class="age-gate__title">Sorry, you must be 16 or older</h1>
        <p class="age-gate__text">
          All titles on DigiLok OTT are rated 16+. We can't let you continue if you are under 16.
          Please come back when you are old enough.
        </p>
        <div class="age-gate__actions">
          <a class="age-gate__btn age-gate__btn--primary" href="https://www.google.com">Leave this site</a>
          <button class="age-gate__btn age-gate__btn--ghost" @click="gate.resetDecline()">
            I clicked the wrong option
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { watch, onUnmounted } from 'vue'
import { useAgeGateStore } from '@/stores/ageGate'

const gate = useAgeGateStore()

// 年龄门展示时锁住页面滚动，确认后恢复
watch(
  () => gate.required,
  (need) => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = need ? 'hidden' : ''
  },
  { immediate: true }
)

onUnmounted(() => {
  if (typeof document !== 'undefined') document.body.style.overflow = ''
})
</script>

<style scoped>
.age-gate {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-md);
  background: rgba(10, 6, 3, 0.96);
  backdrop-filter: blur(6px);
  overflow-y: auto;
}
.age-gate__panel {
  width: 100%;
  max-width: 520px;
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-elevated);
  padding: var(--space-xl) var(--space-lg);
  text-align: center;
}
.age-gate__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 6px 12px;
  margin-bottom: var(--space-md);
  border: 2px solid var(--primary);
  border-radius: 6px;
  color: var(--primary);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 20px;
  letter-spacing: 0.06em;
}
.age-gate__title {
  font-family: var(--font-display);
  font-size: 22px;
  line-height: 1.3;
  color: var(--text-primary);
  margin-bottom: var(--space-md);
}
.age-gate__text {
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-secondary);
  margin-bottom: var(--space-sm);
}
.age-gate__text--sm { font-size: 12px; color: var(--text-muted); }
.age-gate__actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin: var(--space-lg) 0 var(--space-md);
}
.age-gate__btn {
  display: block;
  width: 100%;
  padding: 13px 20px;
  border-radius: var(--radius-sm);
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  text-align: center;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}
.age-gate__btn--primary { background: var(--gradient); color: var(--bg-dark); }
.age-gate__btn--primary:hover { filter: brightness(1.1); }
.age-gate__btn--ghost {
  background: transparent;
  color: var(--text-secondary);
  border-color: var(--border-strong);
}
.age-gate__btn--ghost:hover { color: var(--text-primary); border-color: var(--primary); }
.age-gate__legal {
  font-size: 12px;
  color: var(--text-muted);
}
.age-gate__legal a { color: var(--text-muted); text-decoration: underline; }
.age-gate__legal a:hover { color: var(--primary); }
.age-gate__sep { margin: 0 6px; }
</style>
